import React, { useState } from "react";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import { bool, func, object } from "prop-types";

const steps = [
  "Fill out the invitation form",
  "Share the unique link",
  "Brand accepts & account created",
  "Complete the partnership",
];

/**
 * Modal for inviting external brands to join a campaign
 * @param {Object} props - Component props
 * @param {boolean} props.active - Whether the modal is active
 * @param {Object} props.currentCampaign - Current campaign data
 * @param {Object} props.currentBrand - Current brand data
 * @param {Function} props.closeModal - Function to close the modal
 * @returns {import("react").ReactElement}
 */
export default function ExternalBrandInviteModal(props) {
  const { active, currentCampaign, currentBrand, closeModal } = props;

  const [brandName, setBrandName] = useState("");
  const [brandEmail, setBrandEmail] = useState("");
  const [confirmBrandEmail, setConfirmBrandEmail] = useState("");
  const [brandUrl, setBrandUrl] = useState("");
  const [fullName, setFullName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [inviteUrl, setInviteUrl] = useState(null);
  const [copied, setCopied] = useState(false);

  const resetForm = () => {
    setBrandName("");
    setBrandEmail("");
    setConfirmBrandEmail("");
    setBrandUrl("");
    setFullName("");
    setLoading(false);
    setError(null);
    setSuccess(false);
    setInviteUrl(null);
    setCopied(false);
  };

  const handleClose = () => {
    resetForm();
    closeModal();
  };

  const handleSubmit = async () => {
    // Validation
    if (!brandName.trim()) {
      setError("Brand name is required");
      return;
    }
    if (!brandEmail.trim()) {
      setError("Brand email is required");
      return;
    }
    if (!brandUrl.trim()) {
      setError("Brand URL is required");
      return;
    }
    if (brandEmail.toLowerCase() !== confirmBrandEmail.toLowerCase()) {
      setError("Email addresses do not match");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(
        "/api/external-brand-invite",
        {
          campaignId: currentCampaign.id,
          brandName: brandName.trim(),
          brandEmail: brandEmail.trim(),
          confirmBrandEmail: confirmBrandEmail.trim(),
          brandUrl: brandUrl.trim(),
          fullName: fullName.trim(),
        },
        {
          headers: { brand: currentBrand.id },
        },
      );

      setSuccess(true);
      setInviteUrl(response.data.invite.inviteUrl);
    } catch (err) {
      setError(err.response?.data ?? "Failed to create invite");
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(inviteUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog open={active} onClose={handleClose} fullWidth maxWidth="lg">
      <DialogTitle>Invite External Brand to Campaign</DialogTitle>
      <DialogContent>
        <Box className="mb-4">
          <Typography variant="body1" color="textSecondary" className="mb-4">
            Follow these simple steps below to invite a brand to join your campaign!
          </Typography>

          <Stepper activeStep={success ? 1 : 0} alternativeLabel className="my-4">
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {success ? (
          <Box>
            <Alert severity="success" className="mb-4">
              Invitation sent successfully! An email has been sent to {brandEmail}.
            </Alert>
            <Typography variant="body1" className="mb-2">
              You can also share this link directly with the brand:
            </Typography>
            <Box className="flex items-center gap-2">
              <TextField fullWidth value={inviteUrl} InputProps={{ readOnly: true }} size="small" />
              <IconButton onClick={handleCopyLink} color={copied ? "success" : "default"}>
                <ContentCopyIcon />
              </IconButton>
            </Box>
            {copied && (
              <Typography variant="caption" color="success.main">
                Copied to clipboard!
              </Typography>
            )}
            <Alert severity="info" className="mt-4">
              <Typography variant="body2">
                <strong>What happens next:</strong>
              </Typography>
              <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                <li>The brand clicks the link and fills in their details</li>
                <li>A DojoMojo account is auto-created with a 30-day free trial</li>
                <li>They&apos;re automatically added as an accepted partner</li>
                <li>Partnership agreement is auto-created</li>
              </ul>
            </Alert>
          </Box>
        ) : (
          <Box className="flex flex-col gap-4 pt-2">
            {error && <Alert severity="error">{error}</Alert>}

            <Typography variant="subtitle1" color="primary">
              Step 1: Fill out the short invitation form below
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Add as much information as you can about the brand you want to invite (one that&apos;s
              not already in the Dojo network).
            </Typography>

            <TextField
              label="Brand Name"
              value={brandName}
              onChange={(e) => setBrandName(e.target.value)}
              fullWidth
              required
              placeholder="Enter the brand name"
            />
            <TextField
              label="Brand Website URL"
              value={brandUrl}
              onChange={(e) => setBrandUrl(e.target.value)}
              fullWidth
              required
              placeholder="https://example.com"
            />
            <TextField
              label="Contact Email"
              type="email"
              value={brandEmail}
              onChange={(e) => setBrandEmail(e.target.value)}
              fullWidth
              required
              placeholder="contact@brand.com"
            />
            <TextField
              label="Confirm Contact Email"
              type="email"
              value={confirmBrandEmail}
              onChange={(e) => setConfirmBrandEmail(e.target.value)}
              fullWidth
              required
              placeholder="Re-enter email to confirm"
              error={
                confirmBrandEmail && brandEmail.toLowerCase() !== confirmBrandEmail.toLowerCase()
              }
              helperText={
                confirmBrandEmail && brandEmail.toLowerCase() !== confirmBrandEmail.toLowerCase()
                  ? "Emails do not match"
                  : ""
              }
            />
            <TextField
              label="Contact Name (optional)"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              fullWidth
              placeholder="John Doe"
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{success ? "Close" : "Cancel"}</Button>
        {!success && (
          <Button variant="contained" color="primary" onClick={handleSubmit} disabled={loading}>
            {loading ? "Generating..." : "Generate Invite Link"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

ExternalBrandInviteModal.propTypes = {
  active: bool.isRequired,
  currentCampaign: object.isRequired,
  currentBrand: object.isRequired,
  closeModal: func.isRequired,
};
